import React, { useCallback, useMemo } from 'react';
import type { Tenant, TenantInput } from '#/graphql/types';
import Form, { FormAction } from '#/common/components/Form';
import fields from './fields';
import { useNavigate } from 'react-router-dom';
import useToast from '#/common/hooks/useToast';
import useTenantUpdate from '../../hooks/useUpdateTenantMutation';
import { get } from 'lodash';
import useTenantCreate from '../../hooks/useCreateTenantMutation';
import useTenantDelete from '../../hooks/useDeleteTenantMutation';
import allFields from '../../utils/fields';
import { getCleanFormErrors } from '#/common/utils/customRelatedItemsValidation';
import useSession from '#/common/hooks/useSession';
import useWorkOrderCreate from '#/modules/workOrder/hooks/useWorkOrderCreate';
import { UserRole } from '#/common/types/userTypes';
import syncTisTenantDataMutation from '../../hooks/useSyncTisTenantDataMutation';

const formReadonlyRole: Record<UserRole, boolean> = {
  [UserRole.asset_manager]: true,
  [UserRole.trane_admin]: true,
  [UserRole.technician]: true,
};

const actionsPerUserRole: Record<UserRole, ('fetchTISData' | 'alarm' | 'criticalAlarm')[]> = {
  [UserRole.asset_manager]: ['fetchTISData', 'alarm', 'criticalAlarm'],
  [UserRole.trane_admin]: ['fetchTISData', 'alarm', 'criticalAlarm'],
  [UserRole.technician]: [],
};

export interface TenantFormProps {
  tenant: Tenant | {};
  title: string;
}

const TenantForm: React.FC<TenantFormProps> = ({ tenant, title }) => {
  const { alert, confirm } = useToast();

  const { user } = useSession();
  const role = user?.role as UserRole | undefined;

  const navigate = useNavigate();
  const id = get(tenant, 'id', undefined);

  // Defined in the modules hooks dir
  const { createWorkOrder } = useWorkOrderCreate();
  const { updateTenant } = useTenantUpdate();
  const { createTenant } = useTenantCreate();
  const { deleteTenant } = useTenantDelete();
  const { syncTisTenant } = syncTisTenantDataMutation();

  const deleteItem = useCallback(
    (id: string) => {
      if (!id) {
        return undefined;
      }
      return deleteTenant(id);
    },
    [id, deleteTenant],
  );

  // Submit that updates or creates a new record based on if there is ID present
  const submit = useCallback(
    (data: TenantInput) => {
      if (!id) {
        return createTenant(data);
      }
      return updateTenant(id, data);
    },
    [id, updateTenant, createTenant],
  );

  const isFormReadonly = !role || formReadonlyRole[role];

  const actions = useMemo(() => {
    const result: FormAction<Tenant>[] = [];

    if (!isFormReadonly && deleteItem && id) {
      result.push({
        label: 'Delete',
        icon: 'trash',
        type: 'danger',
        onClick: async (e) => {
          const sure = await confirm(
            'error',
            `Are you sure you want to delete the tenant: ${e.record?.name}?`,
          );

          if (!sure) {
            return;
          }

          try {
            if (!e.record?.id) {
              return;
            }

            e.setIsLoading(true);
            const result = await deleteItem(e.record.id);
            e.setIsLoading(false);
            if (result?.errors) {
              void alert('error', result.errors.join(`;\\n`));
              return;
            }
            navigate('/admin/tenants');
          } catch (e: any) {
            console.log('Error', e);
            void alert('error', "Couldn't delete tenant!");
          }
        },
      });
    }
    if (id) {
      if (role && actionsPerUserRole[role].includes('fetchTISData'))
        result.push({
          label: 'Fetch TIS data',
          type: 'normal',
          onClick: async (e) => {
            const sure = await confirm(
              'warning',
              `Are you sure you want fetch the TIS data for tenant "${e.record?.name}"?`,
            );

            if (!sure) {
              return;
            }

            try {
              e.setIsLoading(true);
              const result = await syncTisTenant([id]);
              e.setIsLoading(false);

              if (result?.errors) {
                throw result.errors;
              }

              void alert(
                'success',
                `Buildings, Assets and Asset properties synced! Findings and Alerts are scheduled for sync and will be done in few minutes.`,
              );
            } catch (e: any) {
              console.log('TIS Sync error', e);
              void alert('error', 'Opps, something unexpected happened!');
            }
          },
        });
    }
    if (!isFormReadonly && submit) {
      result.push({
        label: 'Submit',
        type: 'submit',
        onClick: async (e) => {
          let sure = true;
          const isGroupNameChanged = get(e, 'record.contacts') != get(tenant, 'contacts');
          if (isGroupNameChanged)
            sure = await confirm(
              'warning',
              `Changing the "Gognito Group Name" might cause problems with user access to this tenant, are you sure you want to proceed?`,
            );

          if (!e.record || !sure) {
            return;
          }
          const validation = e.form?.validate();
          if (!validation?.isValid && getCleanFormErrors(validation, allFields, e.record).length) {
            void alert('error', `${title} is not valid, check field entries.`);
            return;
          }

          try {
            e.setIsLoading(true);
            const result = await submit(e.record);
            e.setIsLoading(false);

            if (result?.errors) {
              void alert('error', result.errors.join(`;\\n`));
              return;
            }
            if (!id && result?.data && result?.data.id) {
              navigate(`/tenants/${result.data?.id}`);
            }

            void alert('success', 'Changes saved!');
          } catch (err: any) {
            e.setIsLoading(false);
            console.log('Error', err);
            void alert('error', 'Missing or invalid data!');
          }
        },
      });
    }

    return result;
  }, [alert, confirm, navigate, submit, tenant, deleteItem, user]);
  return <Form record={tenant as Tenant} fields={fields} actions={actions} />;
};

export default React.memo(TenantForm);
